import { Popover, Tooltip } from "@mui/material";
import { ArrowLeft, Eye, Filter, PlusCircle } from "feather-icons-react/build/IconComponents";
import { t } from "i18next";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { CaretDownFill, CaretUpFill } from "react-bootstrap-icons";
import API from "../../API/newIndex";
import { DataLoad, PlanPermission } from "../../App";
import { TableColumnsContext } from "../../controllers/TableColumnsContext";
import { NOPHOTO_IMAGE } from "../../Assets/StaticData/StaticImage";
import Breadcrumbs from "../../controllers/breadCrumbs";
import { formatDate } from "../../controllers/FormatAmount";
import { getInitialPage, replacePageInUrl } from "../../controllers/PageHandleFromQuery";
import { parentNavigate } from "../../controllers/ParentNavigate";
import { useUser } from "../../controllers/UserContext";
import { isAscendingOrder } from "../FormInput/CommonTable";
import {
  ButtonComponent,
  CommonTable,
  PageLimitDropDownComponent,
  PaginationComponent,
  SearchComponent
} from "@shopeasy-dashboard-ui/ui-library";
import NoRecords from "../Main/NoRecords";
import Notification from "../Notification";
import AddBlogComments from "./AddBlogComments";
import * as defaultCustomFields from '../../utilities/defaultCustomTableField';
import useTableApi from "../../utilities/useTableApi";

const BlogComments = () => {
  const handleTableApi = useTableApi();
  const { tableColumnsData } = useContext(TableColumnsContext);
  const { dataLoading, setDataLoading } = useContext(DataLoad);
  const { sortData, isUserLoaded } = useUser();
  const userContext = useUser();
  const savedSort = sortData?.blogcomments || sortData?.comments;

  const [limit, setLimit] = useState(10);
  const [Page, setPage] = useState(getInitialPage());
  const [comments, setComments] = useState();
  const [sortBy, setSortBy] = useState(() => savedSort?.sort_by || "createdAt");
  const [iconVisible, setIconVisible] = useState(false);
  const [orderBy, setOrderBy] = useState(() => {
    const isAsc = isAscendingOrder(savedSort?.order_by);
    return {
      orderByValue: savedSort?.order_by ? (isAsc ? "ASC" : "DESC") : "DESC",
      isDecending: savedSort?.order_by ? !isAsc : true,
    };
  });
  const isSortSynced = useRef(false);

  useEffect(() => {
    if (isUserLoaded && !isSortSynced.current) {
      isSortSynced.current = true;
      if (savedSort && savedSort.sort_by) {
        const isAsc = isAscendingOrder(savedSort.order_by);
        const targetOrderBy = isAsc ? "ASC" : "DESC";
        setSortBy(savedSort.sort_by);
        setOrderBy({
          orderByValue: targetOrderBy,
          isDecending: !isAsc,
        });
      }
    }
  }, [savedSort, isUserLoaded]);
  const [isNotification, setIsNotification] = useState(false);
  const [notificationMsg, setNotificationMsg] = useState("");
  const [severity, setSeverity] = useState();
  const { setPlanPermission } = useContext(PlanPermission);
  const commentRef = useRef(null);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    replacePageInUrl(Page);
  }, [Page]);

  // Sorting Data
  const SortData = (sort_by, explicitOrder) => {
    setSortBy(sort_by);
    let nextOrder = "DESC";
    if (explicitOrder !== undefined) {
      nextOrder = isAscendingOrder(explicitOrder) ? "ASC" : "DESC";
    } else if (sortBy === sort_by) {
      const isCurrentlyAsc = isAscendingOrder(orderBy);
      nextOrder = isCurrentlyAsc ? "DESC" : "ASC";
    } else {
      nextOrder = "DESC";
    }
    setOrderBy({
      orderByValue: nextOrder,
      isDecending: nextOrder === "DESC",
    });
  };

  const [search, setSearch] = useState("");
  var startRecord = (comments?.data?.currentPage - 1) * limit + 1;
  var endRecord = Math.min(comments?.data?.currentPage * limit, comments?.data?.totalRecords);
  const debounceTimeout = useRef(null);

  useEffect(() => {
    if (!isUserLoaded) {
      return;
    }

    if (
      savedSort &&
      savedSort.sort_by &&
      (sortBy !== savedSort.sort_by ||
        orderBy.orderByValue !== (isAscendingOrder(savedSort.order_by) ? "ASC" : "DESC"))
    ) {
      return;
    }

    const trimmed = search?.trim() || "";
    clearTimeout(debounceTimeout.current);
    if (trimmed === "") {
      GetBlogComments();
    } else if (trimmed.length >= 3) {
      debounceTimeout.current = setTimeout(() => {
        GetBlogComments();
      }, 500);
    }
    return () => clearTimeout(debounceTimeout.current);
  }, [Page, limit, orderBy, sortBy, search, isUserLoaded, savedSort]);
  // get comments
  const GetBlogComments = async () => {
    setDataLoading(true);
    try {
      const data = await API({
        url: `/admin/blog/GetBlogComments?searchkey=${search?.length > 2 ? search : ""
          }&page=${Page}&limit=${limit}&sort_by=${sortBy}&order_by=${orderBy.orderByValue}`,
        method: "get",
      });
      const result = data?.data;
      if (data.status === 200 || data.status === 304) {
        setComments(result);
      }
      const totalRecords = Number(result?.data?.totalRecords);
      if (!isNaN(totalRecords)) {
        if (totalRecords === 0) {
          if (Page > 1) {
            setPage(1);
          }
        } else if (Number(startRecord) > totalRecords) {
          setPage(Page - 1);
        }
      }
    } catch (error) {
      if (error?.response?.status === 882) {
        setPlanPermission({ isPlanPermissions: false, forModule: "Blog" });
      }
      setIsNotification(true);
      setNotificationMsg(error);
      setSeverity("error");
    } finally {
      setDataLoading(false);
    }
  };

  const [filter, setFilter] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const handleClose = () => {
    setIsNotification(false);
    setNotificationMsg("");
    setSeverity("");
  };
  const [isAddComment, setIsAddComment] = useState(false);
  const closeAddComment = (value) => {
    if (value === "call api") {
      GetBlogComments();
      setIsAddComment(false);
    } else {
      setIsAddComment(false);
    }
  };
  useEffect(() => {
    if (isAddComment) {
      setTimeout(() => {
        commentRef.current?.focus();
      }, 100);
    }
  }, [isAddComment]);

  const resetFilter = (e) => {
    e.preventDefault();
    setFilter(false);
    setAnchorEl(null);
    setSearch("");
    setPage(1);
    setSortBy("");
    setOrderBy({ orderByValue: "DESC", isDecending: true });
  };
  const limitValue = limit === "all" ? comments?.data?.totalRecords : Number(limit || 10);
  const startRec = comments?.data?.currentPage ? (comments.data.currentPage - 1) * limitValue + 1 : 0;
  const endRec = comments?.data?.currentPage ? Math.min(
    comments.data.currentPage * limitValue,
    comments.data.totalRecords
  ) : 0;

  const apiFieldNames = useMemo(() => {
    const dataObj = tableColumnsData?.data?.data || tableColumnsData?.data || tableColumnsData;
    if (Array.isArray(dataObj?.blogcomment)) return dataObj.blogcomment;
    if (Array.isArray(dataObj?.blogcomments)) return dataObj.blogcomments;
    if (Array.isArray(tableColumnsData?.blogcomment)) return tableColumnsData.blogcomment;
    if (Array.isArray(tableColumnsData?.blogcomments)) return tableColumnsData.blogcomments;
    return [];
  }, [tableColumnsData]);

  const dynamicColumns = useMemo(() => {
    const fixedKeys = new Set([
      '_id', '__v', 'client', 'tenant', 'store_client', 'store_tenant', 'user',
      'image', 'title', 'comment_count', 'action', 'actions',
      'checkbox', 'selection', 'select', 'drag_handle', 'draghandle', 'drag',
    ]);

    return apiFieldNames
      .filter((fieldName) => typeof fieldName === 'string' && fieldName.trim() && !fixedKeys.has(fieldName))
      .map((fieldName) => {
        const isDotted = fieldName.includes('.');
        const parts = isDotted ? fieldName.split('.') : null;

        const label = fieldName
          .replace(/\./g, ' › ')
          .replace(/[-_]/g, ' ')
          .replace(/([a-z])([A-Z])/g, '$1 $2')
          .replace(/\b\w/g, (c) => c.toUpperCase());

        const getValue = (row) => {
          if (!row) return undefined;
          if (isDotted && parts) {
            return parts.reduce((obj, k) => (obj != null ? obj[k] : undefined), row);
          }
          return row[fieldName];
        };

        return {
          key: fieldName,
          title: label,
          width: '160px',
          minWidth: '110px',
          responsiveWidth: '100px',
          headerClassName: 'px-3 whitespace-nowrap truncate',
          cellClassName: 'px-3 truncate',
          render: (row) => {
            const val = getValue(row);
            if (val === null || val === undefined || val === '') return '-';
            if (typeof val === 'boolean') return val ? 'Yes' : 'No';
            if (
              typeof val === 'string' &&
              (fieldName.toLowerCase().includes('date') ||
                fieldName.toLowerCase().includes('createdat') ||
                fieldName.toLowerCase().includes('updatedat') ||
                fieldName.toLowerCase().endsWith('at') ||
                /^\d{4}-\d{2}-\d{2}T/.test(val))
            ) {
              return formatDate(val);
            }
            if (typeof val === 'object') return JSON.stringify(val);
            return String(val);
          },
        };
      });
  }, [apiFieldNames]);

  const columns = [
    {
      key: "image",
      title: t("lang_Image") || "Image",
      width: "75px",
      minWidth: "70px",
      maxWidth: "85px",
      responsiveWidth: "60px",
      headerClassName: "thead-image px-2 text-center",
      cellClassName: "table-image px-2 text-center",
      render: (row) => (
        <img
          width={40}
          height={50}
          onError={(e) => {
            e.target.src = NOPHOTO_IMAGE;
          }}
          src={row.image || NOPHOTO_IMAGE}
          className="p-0.5 max-h-full rounded-md mx-auto block"
          alt={row?.title}
        />
      ),
    },
    {
      key: "title",
      title: t("lang_title") || "Title",
      minWidth: "260px",
      headerClassName: "px-3 text-left",
      cellClassName: "px-3 text-left",
      render: (row) => (
        <p className="line-clamp-2 text-xs font-medium text-[var(--primary-text-color)]">
          {row?.title || "-"}
        </p>
      ),
    },
    {
      key: "comment_count",
      title: t("lang_Comment_Count") || "Comment Count",
      width: "160px",
      minWidth: "140px",
      maxWidth: "180px",
      headerClassName: "px-3 text-right",
      cellClassName: "px-3 text-right",
      align: "text-right",
      render: (row) => (
        <p className="line-clamp-2 w-full text-right text-xs">
          {row?.comment_count || "0"}
        </p>
      ),
    },
    ...dynamicColumns,
    {
      key: "action",
      title: t("lang_action"),
      width: "80px",
      minWidth: "80px",
      maxWidth: "80px",
      headerClassName: "px-3 text-right",
      cellClassName: "px-3 text-right",
      align: "text-right",
      ignoreRowClick: true,
      render: (row) => (
        <div className="flex items-center justify-end w-full space-x-2">
          <Tooltip title="View" arrow>
            <button onClick={() => parentNavigate(`/blog/comments/${row?._id}`)}>
              <Eye className="w-[16px] h-[16px] text-[var(--table-text-color)] hover:text-[var(--primary-text-color)] transition-colors" />
            </button>
          </Tooltip>
        </div>
      ),
    },
  ];

  const tableHeaderFilter = (manageFieldsBtn) => {
    return (dataLoading || (comments?.data?.totalRecords && comments?.data?.totalRecords > 0) || comments?.data?.records?.length > 0 || search) ? (
      <div className="relative w-full border-b main-border-color secondary-bg-color px-3 py-2 flex flex-col pe-3 min-h-[50px]">
        <div className="flex flex-wrap items-center justify-between w-full gap-2 min-h-[38px]">
          <div className="flex items-center gap-2 ml-auto hidden md:flex">
            <div className="w-[180px] md:w-[200px]">
              <SearchComponent
                placeholder="Search Blog Comments"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                onClear={() => {
                  setSearch("");
                  setPage(1);
                }}
                isClearable={true}
                size="sm"
                iconPlacement="left"
              />
            </div>
            <div className="w-[80px] md:w-[100px] shrink-0 select-sm-height select-hide-label select-no-padding-y">
              <PageLimitDropDownComponent
                limit={limit}
                setLimit={setLimit}
                setPage={setPage}
                totalRecords={comments?.data?.totalRecords || 0}
              />
            </div>
            {manageFieldsBtn}
          </div>

          <Tooltip title={t("lang_filter") || "Filter"} arrow>
            <button
              type="button"
              onClick={(e) => {
                setFilter(true);
                setAnchorEl(e.currentTarget);
              }}
              className="block md:hidden relative flex items-center justify-center p-2 border main-border-color rounded-lg bg-[var(--secondary-bg-color)] hover:bg-gray-50/50 transition-colors w-8 h-8 shrink-0 ml-auto"
            >
              <Filter className="h-4 w-4 text-[var(--table-text-color)]" />
            </button>
          </Tooltip>
        </div>
      </div>
    ) : null;
  };

  return (
    <>
      {isNotification && notificationMsg && (
        <Notification message={notificationMsg} close={handleClose} severity={severity} />
      )}
      <div className="h-auto overflow-y-auto overflow-x-hidden pb-[64px] p-5">
        <div className="flex items-end justify-between page-header-pb">
          <div className="flex items-center space-x-2">
            <button
              onClick={(e) => {
                e.preventDefault();
                parentNavigate("/dashboard");
              }}
              className="back-arrow -ml-3.5"
            >
              <ArrowLeft />
            </button>
            <div>
              <Breadcrumbs
                crumbs={[
                  { name: t("lang_dashboard"), path: `/dashboard` },
                  { name: t("lang_Blog_Comments"), path: "/blog/comments" },
                ]}
              />
              <h4 className="text-xl md:text-2xl font-semibold text-primary">
                {t("lang_Blog_Comments")}
              </h4>
            </div>
          </div>
          <div>
            <ButtonComponent
              variant="primary"
              icon={<PlusCircle className="w-4 h-4" />}
              onClick={(e) => {
                e.preventDefault();
                setIsAddComment(true);
              }}
            >
              {t("lang_Add_Blog_Comments")}
            </ButtonComponent>
          </div>
        </div>

        <div className="flex justify-between w-full space-x-4">
          <div className="w-full space-y-3">
            <div className="flex w-full h-full overflow-hidden relative min-w-0">
              <div className="w-full">
                <div className="w-full transition-all duration-100 mb-[10px]">
                  <CommonTable
                    tableId="blogcomments"
                    columns={columns}
                    data={comments?.data?.records}
                    tableHeader={tableHeaderFilter}
                    onSort={SortData}
                    sortBy={sortBy}
                    orderBy={orderBy}
                    tableFooter={
                      comments?.data && comments?.data?.totalRecords > 0 ? (
                        <PaginationComponent
                          count={comments?.data?.totalPages || 1}
                          page={Page}
                          onChange={(e, v) => setPage(v)}
                          totalRecords={comments?.data?.totalRecords}
                          startRecord={startRec}
                          endRecord={endRec}
                          limit={limit}
                          recordsLabel="Records"
                        />
                      ) : null
                    }
                    loading={dataLoading}
                    emptyMessage={<NoRecords />}
                    rowClick={(row) => parentNavigate(`/blog/comments/${row?._id}`)}
                    containerClassName="blogcomments-table sticky-table module-table overflow-x-auto border border-[var(--border-color)] rounded-xl shadow-[var(--shadow-card)]"
                    NEWAPI={handleTableApi}
                    tableDefaults={defaultCustomFields?.defaultBlogCommentsField}
                    tableWiseAllowedSortKeys={defaultCustomFields?.BLOG_COMMENTS_SORT_KEYS}
                    userState={userContext}
                  />
                </div>
              </div>

              <Popover
                open={filter}
                anchorEl={anchorEl}
                onClose={() => {
                  setFilter(false);
                  setAnchorEl(null);
                }}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                PaperProps={{
                  style: { marginTop: "8px", minWidth: "300px", borderRadius: "8px" },
                }}
                className="rounded-[6px]"
              >
                <div className="p-[20px] filter secondary-bg-color">
                  <div>
                    <div className="flex justify-between items-center">
                      <p className="flex font-semibold text-sm m-0">
                        <Filter className="me-1 w-4 h-4" />
                        {t("lang_filter")}
                      </p>
                      <button
                        type="button"
                        onClick={(e) => resetFilter(e)}
                        className="text-white bg-gray-800 hover:bg-gray-900 focus:outline-none font-medium rounded-md text-sm px-5 py-2 me-1 mb-2 dark:bg-gray-800 dark:hover:bg-gray-700 dark:focus:ring-gray-700 dark:border-gray-700"
                      >
                        Reset Filter
                      </button>
                    </div>
                  </div>
                  <hr className="main-border-color my-3"></hr>
                  <div className="grid gap-y-5 table-text">
                    <div>
                      <span className="text-sm capitalize font-medium mb-1 inline-block">
                        {t("lang_Search")}
                      </span>
                      <SearchComponent
                        placeholder="Search Blog Comments"
                        value={search}
                        onChange={(e) => {
                          setSearch(e.target.value);
                          setPage(1);
                        }}
                        onClear={() => {
                          setSearch("");
                          setPage(1);
                        }}
                        iconPlacement="left"
                        isClearable={true}
                      />
                    </div>
                    <div>
                      <span className="text-sm table-text font-medium mb-1 inline-block">
                        {t("lang_set_items_limit")}
                      </span>
                      <PageLimitDropDownComponent
                        limit={limit}
                        setLimit={setLimit}
                        setPage={setPage}
                        totalRecords={comments?.data?.totalRecords || 0}
                      />
                    </div>
                  </div>
                </div>
              </Popover>
            </div>
          </div>
        </div>
      </div>
      <AddBlogComments
        commentRef={commentRef}
        isAddComment={isAddComment}
        closeAddComment={closeAddComment}
        type="add"
      />
    </>
  );
};

export default BlogComments;
